import {
  Box,
  Avatar,
  Text,
  Tooltip,
  type AvatarProps,
  Popover,
} from '@mantine/core'
import Link from 'next/link'
import { useEffect, useState, useMemo } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreProduct } from '@flecto/types'

import { Badge, AvatarProduct, TextProductTitle, TextProductQuantity } from '..'
import { usePopoverTrackedState } from '../PopoverTracked/usePopoverTrackedState'
import classes from './styles.module.css'

export type AvatarProductsGroupProps = Pick<AvatarProps, 'size' | 'radius'> & {
  products?: CoreProduct[]
  withLimit?: number
}

export function AvatarProductsGroup({
  products = [],
  radius,
  size = 'md',
  withLimit = 4,
}: AvatarProductsGroupProps) {
  const { t } = useTranslation('shared', { keyPrefix: 'shared' })
  const { popoverIdOpened, setPopoverIdOpened, isNonExclusive } =
    usePopoverTrackedState()

  const popoverId = useMemo(() => {
    const productIds = (products ?? [])
      .map((p) => p.id)
      .sort()
      .join('-')
    return `popover-avatar-group-${productIds || 'empty'}`
  }, [products])

  const [localOpened, setLocalOpened] = useState(false)

  const popoverOpened = isNonExclusive()
    ? localOpened
    : localOpened && popoverIdOpened === popoverId

  useEffect(() => {
    if (
      !isNonExclusive() &&
      popoverIdOpened &&
      popoverIdOpened !== popoverId &&
      localOpened
    ) {
      setLocalOpened(false)
    }
  }, [popoverIdOpened, popoverId, localOpened])

  // const filteredProducts = (products ?? []).filter((product) => {
  //   if (product.bundle) {
  //     return true
  //   }

  //   const isBundleItem = (products ?? []).some((bundleProduct) => {
  //     if (
  //       !bundleProduct.bundle ||
  //       !bundleProduct.bundle_attributes?.bundle_items
  //     ) {
  //       return false
  //     }

  //     return bundleProduct.bundle_attributes.bundle_items.some((bundleItem) => {
  //       return bundleItem.item.id === product.id
  //     })
  //   })

  //   return !isBundleItem
  // })

  const localProducts = products.slice(0, withLimit)

  const exceedingProducts =
    localProducts.length > withLimit ? localProducts.slice(withLimit) : []

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newOpened = !popoverOpened
    setLocalOpened(newOpened)

    if (!isNonExclusive()) {
      if (newOpened) {
        setPopoverIdOpened(popoverId)
      } else {
        setPopoverIdOpened(null)
      }
    }
  }

  const redirectToProduct = (product: CoreProduct) => {
    if (product.bundle) {
      return `/products/bundle/${product.internal_id}`
    }
    return `/products/item/${product.internal_id}`
  }

  const handlePopoverChange = (opened: boolean) => {
    setLocalOpened(opened)

    if (!isNonExclusive()) {
      if (opened) {
        setPopoverIdOpened(popoverId)
      } else {
        setPopoverIdOpened(null)
      }
    }
  }

  if (localProducts.length <= 0) {
    return (
      <Badge color='gray' data-testid='no-products'>
        {t('label_no_products')}
      </Badge>
    )
  }

  return (
    <Popover
      position='bottom'
      withArrow
      shadow='md'
      opened={popoverOpened}
      onChange={handlePopoverChange}
      trapFocus={false}
      closeOnEscape={true}
      withinPortal={true}
    >
      <Popover.Target>
        <Avatar.Group
          spacing='sm'
          w='fit-content'
          style={{ cursor: 'pointer' }}
          onClick={handleClick}
        >
          {localProducts.map((product) => (
            <Tooltip
              key={`avatar-product-${product?.id}`}
              label={
                <Text truncate>
                  <TextProductQuantity
                    product={product}
                    className={classes.quantity}
                    withX='after'
                    display='inline'
                    mr='xs'
                  />
                  {product?.title}
                </Text>
              }
              maw={250}
              withinPortal
            >
              <Box>
                <AvatarProduct product={product} size={size} />
              </Box>
            </Tooltip>
          ))}

          {exceedingProducts.length > 0 && (
            <Avatar size='md' radius={radius}>
              <Text fw={500} size='sm'>
                +{exceedingProducts.length}
              </Text>
            </Avatar>
          )}
        </Avatar.Group>
      </Popover.Target>
      <Popover.Dropdown
        miw={300}
        py='md'
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
        }}
        style={{ width: 332, maxHeight: 400, overflowY: 'auto' }}
      >
        {exceedingProducts.map((p, i) => (
          <Box
            key={`exceeding-product-${p?.id}`}
            className={classes.item}
            mt={i > 0 ? 'md' : 0}
          >
            <AvatarProduct product={p} className={classes.avatar} />
            <Link
              href={redirectToProduct(p)}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                setLocalOpened(false)
                if (!isNonExclusive()) {
                  setPopoverIdOpened(null)
                }
              }}
              target='_blank'
              rel='noopener noreferrer'
            >
              <TextProductTitle
                product={p}
                className={classes.title}
                truncate
              />
            </Link>
            <Text className={classes.id}>ID: {p.internal_id}</Text>
            <TextProductQuantity
              product={p}
              className={classes.quantity}
              withX='before'
            />
          </Box>
        ))}
      </Popover.Dropdown>
    </Popover>
  )
}
